export const PLAYABLE_COVER_WIDTHS = [320, 640, 960] as const;

export type PlayableCoverWidth = (typeof PLAYABLE_COVER_WIDTHS)[number];

const PLAYABLE_COVER_CDN_HOST = "stream.muhaha.ai";
const PROCESSABLE_IMAGE_PATH = /\.(?:jpe?g|png|webp)$/i;

export interface PlayableCoverImageAttributes {
	src: string;
	srcSet?: string;
	sizes?: string;
}

/**
 * CDN image processing currently ignores extensionless blob URLs. Keeping the
 * eligibility check here prevents three cache keys from serving the same original.
 */
export function isProcessablePlayableCover(source: string): boolean {
	try {
		const url = new URL(source);
		return (
			url.protocol === "https:" &&
			url.hostname === PLAYABLE_COVER_CDN_HOST &&
			PROCESSABLE_IMAGE_PATH.test(url.pathname) &&
			!url.searchParams.has("image_process")
		);
	} catch {
		return false;
	}
}

/** Build one Alibaba Cloud CDN resize URL while preserving the original query and hash. */
export function playableCoverUrl(source: string, width: PlayableCoverWidth): string {
	if (!isProcessablePlayableCover(source)) return source;

	const hashIndex = source.indexOf("#");
	const base = hashIndex === -1 ? source : source.slice(0, hashIndex);
	const hash = hashIndex === -1 ? "" : source.slice(hashIndex);
	const separator = base.includes("?")
		? base.endsWith("?") || base.endsWith("&")
			? ""
			: "&"
		: "?";

	// The CDN does not execute the command when URLSearchParams encodes this comma as %2C.
	return `${base}${separator}image_process=resize,w_${width}${hash}`;
}

export function playableCoverSrcSet(source: string): string | undefined {
	if (!isProcessablePlayableCover(source)) return undefined;
	return PLAYABLE_COVER_WIDTHS.map(
		(width) => `${playableCoverUrl(source, width)} ${width}w`,
	).join(", ");
}

export function playableCoverImageProps(
	source: string,
	sizes: string,
	fallbackWidth: PlayableCoverWidth = 640,
): PlayableCoverImageAttributes {
	const srcSet = playableCoverSrcSet(source);
	if (!srcSet) return { src: source };
	return {
		src: playableCoverUrl(source, fallbackWidth),
		srcSet,
		sizes,
	};
}
